import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";

// Splash only appears when something is actually slow: the session check
// must exceed SPLASH_DELAY_MS, and "waking servers…" only shows while the
// /api/ping warm-up is still pending after COLD_THRESHOLD_MS.
const SPLASH_DELAY_MS = 400;
const COLD_THRESHOLD_MS = 1500;

export default function SplashGate({ children }) {
  const { ready } = useAuth();
  const [cold, setCold] = useState(false);
  const [showSplash, setShowSplash] = useState(false);

  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    let done = false;
    const coldTimer = setTimeout(() => { if (!done) setCold(true); }, COLD_THRESHOLD_MS);
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 30000);
    fetch(`${backend}/api/ping`, { signal: controller.signal })
      .catch(() => {})
      .finally(() => { done = true; clearTimeout(coldTimer); clearTimeout(abortTimer); setCold(false); });

    // 60s heartbeat while the tab is visible keeps the backend warm.
    let heartbeatId = null;
    const ping = () => {
      if (document.visibilityState !== "visible") return;
      fetch(`${backend}/api/ping`).catch(() => {});
    };
    const start = () => { if (!heartbeatId) heartbeatId = setInterval(ping, 60000); };
    const stop = () => { if (heartbeatId) { clearInterval(heartbeatId); heartbeatId = null; } };
    start();
    const onVis = () => { if (document.visibilityState === "visible") { ping(); start(); } else { stop(); } };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      controller.abort();
      clearTimeout(coldTimer);
      clearTimeout(abortTimer);
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => setShowSplash(true), SPLASH_DELAY_MS);
    return () => clearTimeout(t);
  }, [ready]);

  if (ready) {
    return (
      <>
        {children}
        {cold && <WakingPill />}
      </>
    );
  }
  if (!showSplash) return <div style={{ minHeight: "100vh", background: "#0a0a0a" }} data-testid="splash-pending" />;
  return <Splash label={cold ? "waking servers…" : "loading…"} />;
}

function WakingPill() {
  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[3000] px-3 py-1.5 rounded-full text-[10px] font-mono tracking-widest text-white/70 bg-black/80 border border-white/10 backdrop-blur"
      data-testid="waking-servers-pill"
    >
      waking servers…
    </div>
  );
}

function Splash({ label }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 22,
      }}
      data-testid="splash-screen"
    >
      <div style={{ animation: "crowFloat 2.4s ease-in-out infinite" }}>
        <img
          src="/crow-standing.jpg"
          alt="CROW"
          width={140}
          height={140}
          style={{ display: "block", objectFit: "contain", borderRadius: 12, filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.12))" }}
          data-testid="splash-crow-logo"
        />
      </div>
      <div style={{ color: "#111", fontWeight: 800, letterSpacing: "0.18em", fontFamily: "monospace", fontSize: 12 }}>
        C.R.O.W.
      </div>
      <div style={{ color: "#666", fontFamily: "monospace", fontSize: 10, letterSpacing: "0.14em" }} data-testid="splash-status">
        {label}
      </div>
      <style>{`
        @keyframes crowFloat {
          0%, 100% { transform: translateY(0); opacity: 0.9; }
          50%      { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
